import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, Copy, Download, KeyRound, LoaderCircle, Lock, MailCheck, PenLine, Sparkles, UserPlus, Wallet,
} from 'lucide-react';
import logo from '../assets/1.png';
import CountryFlag from '../components/CountryFlag';
import { ContractDocument } from '../components/contract/ContractDocument';
import { contractSourceFor, parseSections } from '../components/contract/contractText';
import { contractFileName, renderContractPdf } from '../components/contract/contractPdf';
import partnerService, { type PartnerDestination, type PartnerStudentCreated } from '../services/partnerContract.service';
import type { PublicQuotePreview } from '../services/publicContract.service';
import studentPortalService from '../services/studentPortal.service';
import type { ContractDetails, PricingCatalogue, PricingQuestion } from '../types/studentPortal';
import '../layout/StudentPortalLayout.css';
import './PublicContract.css';

/*
 * The partner page (/samir-contract). Behind an access code, a partner registers a
 * student on a normal or special package. The server creates the account, accepts
 * the contract for the student and emails them their sign-in details; the student
 * signs in to a ready contract and only pays the deposit.
 */

const CODE_KEY = 'edunasser.partner.code.v1';
const LOGIN_URL = 'https://edunasser.com/login';
const QUALIFICATIONS = ['شهادة الثانوية العامة', 'دبلوم', 'بكالوريوس', 'ماجستير', 'أخرى'];
const STUDY_LEVELS = ['بكالوريوس', 'ماجستير', 'دكتوراه'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type Step = 0 | 1 | 2;
interface Choice { countryId: number; offerKey: string | null }

interface PersonForm {
  first_name: string; last_name: string; email: string; phone: string; nationality: string; national_id: string;
  passport_number: string; address: string; qualification: string; study_level: string;
  guardian_name: string; guardian_national_id: string; guardian_phone: string; file_notes: string;
}
const EMPTY_PERSON: PersonForm = {
  first_name: '', last_name: '', email: '', phone: '', nationality: '', national_id: '', passport_number: '', address: '',
  qualification: '', study_level: '', guardian_name: '', guardian_national_id: '', guardian_phone: '', file_notes: '',
};

const STEPS = [{ title: 'الباقة', icon: Wallet }, { title: 'بيانات الطالب', icon: PenLine }, { title: 'الحساب والعقد', icon: CheckCircle2 }];

const readCode = () => { try { return sessionStorage.getItem(CODE_KEY) || ''; } catch { return ''; } };
const writeCode = (value: string | null) => { try { if (value) sessionStorage.setItem(CODE_KEY, value); else sessionStorage.removeItem(CODE_KEY); } catch { /* storage unavailable */ } };

const isVisible = (question: PricingQuestion, answers: Record<string, string>) =>
  !question.visible_when || Object.entries(question.visible_when).every(([key, value]) => answers[key] === value);
const money = (value: string | number, currency: string) => `${Number(value).toLocaleString('ar-EG')} ${currency}`;
const countryNameAr = (code: string, fallback: string) =>
  ({ IT: 'إيطاليا', ITA: 'إيطاليا', RO: 'رومانيا', ROU: 'رومانيا', DE: 'ألمانيا', DEU: 'ألمانيا' } as Record<string, string>)[code.toUpperCase()] || fallback;

function errorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const detail = error.response?.data?.detail;
  return typeof detail === 'object' && detail ? (detail as { code?: string }).code : undefined;
}

function errorMessage(error: unknown, fallback: string): string {
  const code = errorCode(error);
  if (code === 'INVALID_ACCESS_CODE') return 'كود الدخول غير صحيح.';
  if (code === 'TOO_MANY_ATTEMPTS') return 'محاولات كثيرة بكود خاطئ. حاول مرة أخرى بعد 15 دقيقة.';
  if (code === 'EMAIL_TAKEN') return 'يوجد حساب بهذا البريد الإلكتروني بالفعل. استخدم بريدًا آخر.';
  if (code === 'INVALID_PRICING_ANSWERS') return 'راجع اختيارات الباقة ثم حاول مرة أخرى.';
  if (code === 'INVALID_SPECIAL_OFFER') return 'هذه الباقة الخاصة غير متاحة لهذه الدولة.';
  if (axios.isAxiosError(error) && error.response?.status === 422) return 'بعض البيانات غير صحيحة. راجع النموذج وحاول مرة أخرى.';
  if (axios.isAxiosError(error) && !error.response) return 'تعذر الاتصال بالخادم. تحقق من الإنترنت وحاول مرة أخرى.';
  return fallback;
}

function validatePerson(form: PersonForm): string | null {
  if (form.first_name.trim().length < 2 || form.last_name.trim().length < 2) return 'اكتب الاسم الأول واسم العائلة.';
  if (!EMAIL_PATTERN.test(form.email.trim())) return 'اكتب بريدًا إلكترونيًا صحيحًا للطالب.';
  const rules: Array<[keyof PersonForm, string, number]> = [
    ['phone', 'رقم الهاتف', 6], ['nationality', 'الجنسية', 2], ['national_id', 'الرقم القومي / رقم الهوية', 5],
    ['address', 'العنوان', 5], ['qualification', 'المؤهل الدراسي', 2], ['study_level', 'المرحلة المطلوبة', 2],
  ];
  for (const [key, label, min] of rules) if (form[key].trim().length < min) return `${label}: أدخل ${min} أحرف على الأقل.`;
  return null;
}

export default function SamirContract() {
  const [code, setCode] = useState(readCode);
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [checking, setChecking] = useState(Boolean(readCode()));

  const [step, setStep] = useState<Step>(0);
  const [destinations, setDestinations] = useState<PartnerDestination[]>([]);
  const [catalogues, setCatalogues] = useState<Record<number, PricingCatalogue>>({});
  const [choice, setChoice] = useState<Choice | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [quote, setQuote] = useState<PublicQuotePreview | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [person, setPerson] = useState<PersonForm>(EMPTY_PERSON);
  const [created, setCreated] = useState<PartnerStudentCreated | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);

  const lockOut = (message: string) => { writeCode(null); setCode(''); setUnlocked(false); setError(message); };

  // Load destinations once the code works; a stored code is re-checked this way.
  useEffect(() => {
    if (!code) { setChecking(false); return; }
    let active = true;
    setChecking(true);
    (async () => {
      try {
        const rows = await partnerService.destinations(code);
        const loaded = await Promise.allSettled(rows.map(async (row) => [row.country_id, await studentPortalService.getPricingCatalogue(row.country_id)] as const));
        if (!active) return;
        setDestinations(rows);
        setCatalogues(Object.fromEntries(loaded.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []))));
        setUnlocked(true);
      } catch (loadError) {
        if (!active) return;
        if (errorCode(loadError) === 'INVALID_ACCESS_CODE') lockOut('انتهت صلاحية الكود أو تم تغييره. أدخل الكود مرة أخرى.');
        else setError(errorMessage(loadError, 'تعذر تحميل الباقات. حدّث الصفحة وحاول مرة أخرى.'));
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [code]);

  const submitCode = async () => {
    const value = codeInput.trim();
    if (!value) return;
    setChecking(true);
    setError(null);
    try {
      await partnerService.openSession(value);
      writeCode(value);
      setCode(value);
    } catch (codeError) {
      setError(errorMessage(codeError, 'تعذر التحقق من الكود.'));
      setChecking(false);
    }
  };

  const destination = destinations.find((d) => d.country_id === choice?.countryId) ?? null;
  const offer = destination?.offers.find((o) => o.key === choice?.offerKey) ?? null;
  const catalogue = choice ? catalogues[choice.countryId] : undefined;
  // An offer fixes its language track and is paid in stages, so those questions are not asked.
  const hidden = useMemo(() => new Set([...(offer ? ['advance_payment'] : []), ...(offer?.language_status ? ['language_status'] : [])]), [offer]);
  const questions = useMemo(
    () => catalogue?.questions.filter((q) => !hidden.has(q.key) && isVisible(q, answers)) ?? [],
    [catalogue, hidden, answers],
  );
  const visibleAnswers = useMemo(
    () => Object.fromEntries(questions.filter((q) => answers[q.key]).map((q) => [q.key, answers[q.key]])),
    [questions, answers],
  );
  const answersComplete = Boolean(catalogue) && questions.every((q) => !q.required || answers[q.key]);

  useEffect(() => {
    if (!choice || !answersComplete || !code) { setQuote(null); return; }
    let active = true;
    setQuoting(true);
    const timer = window.setTimeout(() => {
      partnerService.quote(code, choice.countryId, visibleAnswers, choice.offerKey)
        .then((result) => { if (active) { setQuote(result); setError(null); } })
        .catch((quoteError) => { if (active) { setQuote(null); setError(errorMessage(quoteError, 'تعذر حساب السعر.')); } })
        .finally(() => { if (active) setQuoting(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [choice, answersComplete, visibleAnswers, code]);

  const goTo = (next: Step) => { setError(null); setStep(next); topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const pick = (next: Choice) => { setChoice(next); setAnswers({}); setQuote(null); setError(null); };
  const setField = (key: keyof PersonForm, value: string) => { setError(null); setPerson((p) => ({ ...p, [key]: value })); };

  const register = async () => {
    const invalid = validatePerson(person);
    if (invalid) { setError(invalid); return; }
    if (!choice || !quote) { goTo(0); return; }
    setSubmitting(true);
    setError(null);
    const optional = (value: string) => value.trim() || null;
    const details: ContractDetails = {
      nationality: person.nationality.trim(), national_id: person.national_id.trim(), passport_number: optional(person.passport_number),
      address: person.address.trim(), phone: person.phone.trim(), guardian_name: optional(person.guardian_name),
      guardian_national_id: optional(person.guardian_national_id), guardian_phone: optional(person.guardian_phone),
      qualification: person.qualification, desired_specialization: null, study_level: person.study_level, file_notes: optional(person.file_notes),
    };
    try {
      const result = await partnerService.register(code, {
        ...details, email: person.email.trim(), first_name: person.first_name.trim(), last_name: person.last_name.trim(),
        country_id: choice.countryId, answers: visibleAnswers, special_offer: choice.offerKey,
      });
      setCreated(result);
      goTo(2);
    } catch (registerError) {
      if (errorCode(registerError) === 'INVALID_ACCESS_CODE') lockOut('انتهت صلاحية الكود. أدخله مرة أخرى ثم أعد المحاولة.');
      else setError(errorMessage(registerError, 'تعذر إنشاء حساب الطالب. حاول مرة أخرى.'));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); window.setTimeout(() => setCopied(null), 1500); } catch { /* clipboard unavailable */ }
  };

  const downloadPdf = async () => {
    if (!created || !documentRef.current) return;
    setDownloading(true);
    try { (await renderContractPdf(documentRef.current)).save(contractFileName(created.contract)); }
    catch { setError('تعذر إنشاء ملف PDF. حاول مرة أخرى.'); }
    finally { setDownloading(false); }
  };

  const startOver = () => { setCreated(null); setChoice(null); setAnswers({}); setQuote(null); setPerson(EMPTY_PERSON); goTo(0); };

  const message = created ? [
    `أهلاً ${created.name}،`,
    'تم إنشاء حسابك على EduNasser وتجهيز عقدك.',
    `رابط الدخول: ${LOGIN_URL}`,
    `البريد الإلكتروني: ${created.email}`,
    `كلمة المرور: ${created.password}`,
    `باقتك: ${created.package_title_ar} — ${money(created.payable_total, created.currency)}`,
    `بعد تسجيل الدخول هتلاقي العقد جاهز، والخطوة الجاية سداد العربون ${money(created.deposit_amount, created.currency)} من صفحة المدفوعات.`,
  ].join('\n') : '';

  const sections = useMemo(() => parseSections(contractSourceFor(created?.contract.template_key, created?.contract.country.code)), [created?.contract.template_key, created?.contract.country.code]);

  return (
    <div className="public-contract" dir="rtl" lang="ar">
      <header className="public-contract-header">
        <span className="public-contract-brand"><img src={logo} alt="EduNasser" /></span>
        <div>
          <h1>تسجيل طلاب — عقود سمير</h1>
          <p>اختار الباقة، اكتب بيانات الطالب، وهيتعمله حساب جاهز بالعقد ويوصله الإيميل وكلمة المرور.</p>
        </div>
      </header>

      <main className="public-contract-main" ref={topRef}>
        {error && <div className="portal-alert portal-alert-error" role="alert"><div><strong>تعذر إكمال الخطوة</strong><p>{error}</p></div></div>}

        {!unlocked ? (
          <section className="portal-card portal-card-body public-contract-gate">
            {checking ? <div className="portal-empty"><LoaderCircle className="portal-spin" /></div> : (
              <form onSubmit={(e) => { e.preventDefault(); void submitCode(); }}>
                <h2 className="public-contract-heading"><Lock size={20} /> أدخل كود الدخول</h2>
                <p className="public-contract-lead">الصفحة دي مخصصة للشركاء فقط.</p>
                <div className="portal-field">
                  <label htmlFor="partner-code">كود الدخول</label>
                  <input id="partner-code" className="portal-input" type="password" dir="ltr" autoComplete="off" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} autoFocus />
                </div>
                <div className="public-contract-actions"><span /><button type="submit" className="portal-button portal-button-primary" disabled={!codeInput.trim()}><KeyRound size={17} />دخول</button></div>
              </form>
            )}
          </section>
        ) : (
          <>
            <ol className="public-contract-steps public-contract-steps-3" aria-label="الخطوات">
              {STEPS.map((item, index) => {
                const Icon = item.icon;
                const state = index < step ? 'done' : index === step ? 'current' : 'todo';
                return <li key={item.title} className={`public-contract-step ${state}`}><span className="public-contract-step-icon">{state === 'done' ? <Check size={16} /> : <Icon size={16} />}</span><span className="public-contract-step-title">{item.title}</span></li>;
              })}
            </ol>

            {step === 0 && (
              <section className="portal-card portal-card-body">
                <h2 className="public-contract-heading">اختار الباقة</h2>
                <div className="public-contract-packages">
                  {destinations.map((d) => (
                    <div key={d.country_id} className="public-contract-package-group">
                      <div className="public-contract-package-country"><CountryFlag code={d.code} label={d.name} /><strong>{countryNameAr(d.code, d.name)}</strong></div>
                      <div className="public-contract-options">
                        {[{ key: null as string | null, label: 'الباقة العادية' }, ...d.offers.map((o) => ({ key: o.key as string | null, label: o.title_ar }))].map((item) => {
                          const selected = choice?.countryId === d.country_id && choice.offerKey === item.key;
                          return (
                            <button key={item.key ?? 'normal'} type="button" aria-pressed={selected} onClick={() => pick({ countryId: d.country_id, offerKey: item.key })}
                              className={`public-contract-option ${selected ? 'selected' : ''} ${item.key ? 'special' : ''}`}>
                              {item.key ? <Sparkles size={15} /> : null}<span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {choice && catalogue && (
                  <div className="public-contract-questions public-contract-questions-spaced">
                    {questions.map((question) => (
                      <fieldset key={question.key} className="public-contract-question">
                        <legend>{question.label_ar || question.label}{question.required ? ' *' : ''}</legend>
                        <div className="public-contract-options">
                          {question.options.map((option) => (
                            <label key={option.value} className={`public-contract-option ${answers[question.key] === option.value ? 'selected' : ''}`}>
                              <input type="radio" name={question.key} value={option.value} checked={answers[question.key] === option.value} onChange={() => setAnswers((a) => ({ ...a, [question.key]: option.value }))} />
                              <span>{option.label_ar || option.label}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                    {offer && <p className="public-contract-privacy"><Sparkles size={16} />{offer.language_status ? 'الباقة الخاصة تشمل شهادة اللغة وكل الخدمات، وتُسدد على مراحل.' : 'الباقة الخاصة تُسدد على مراحل.'}</p>}
                  </div>
                )}

                {choice && (
                  <div className="public-contract-price" aria-live="polite">
                    {!answersComplete ? <p>أجب عن الأسئلة المطلوبة علشان يظهر السعر.</p>
                      : quoting && !quote ? <p><LoaderCircle className="portal-spin" size={16} /> جاري حساب السعر...</p>
                      : quote ? (
                        <>
                          <div className="public-contract-price-total"><small>{quote.package_title || 'إجمالي الباقة'}</small><strong>{money(quote.payable_total, quote.currency)}</strong></div>
                          <ul className="public-contract-schedule">{quote.payment_schedule.map((row, i) => <li key={i}><span>{row.title}</span><b>{money(row.amount, quote.currency)}</b></li>)}</ul>
                        </>
                      ) : null}
                  </div>
                )}

                <div className="public-contract-actions"><span /><button type="button" className="portal-button portal-button-primary" disabled={!quote || quoting} onClick={() => goTo(1)}>متابعة لبيانات الطالب<ArrowLeft size={17} /></button></div>
              </section>
            )}

            {step === 1 && (
              <section className="portal-card portal-card-body">
                <h2 className="public-contract-heading">بيانات الطالب</h2>
                <p className="public-contract-lead">هتظهر في العقد، والإيميل هيوصله عليه بيانات الدخول.</p>
                <div className="portal-form-grid">
                  <Field label="الاسم الأول *" value={person.first_name} onChange={(v) => setField('first_name', v)} />
                  <Field label="اسم العائلة *" value={person.last_name} onChange={(v) => setField('last_name', v)} />
                  <Field label="البريد الإلكتروني للطالب *" type="email" dir="ltr" value={person.email} onChange={(v) => setField('email', v)} />
                  <Field label="رقم الهاتف *" type="tel" dir="ltr" value={person.phone} onChange={(v) => setField('phone', v)} />
                  <Field label="الجنسية *" value={person.nationality} onChange={(v) => setField('nationality', v)} />
                  <Field label="الرقم القومي / رقم الهوية *" dir="ltr" value={person.national_id} onChange={(v) => setField('national_id', v)} />
                  <Field label="رقم جواز السفر (إن وجد)" dir="ltr" value={person.passport_number} onChange={(v) => setField('passport_number', v)} />
                  <Field label="العنوان *" value={person.address} onChange={(v) => setField('address', v)} />
                  <Select label="المؤهل الدراسي *" value={person.qualification} options={QUALIFICATIONS} onChange={(v) => setField('qualification', v)} />
                  <Select label="المرحلة المطلوبة *" value={person.study_level} options={STUDY_LEVELS} onChange={(v) => setField('study_level', v)} />
                  <Field label="اسم ولي الأمر (اختياري)" value={person.guardian_name} onChange={(v) => setField('guardian_name', v)} />
                  <Field label="هاتف ولي الأمر" type="tel" dir="ltr" value={person.guardian_phone} onChange={(v) => setField('guardian_phone', v)} />
                  <Field label="رقم هوية ولي الأمر" dir="ltr" value={person.guardian_national_id} onChange={(v) => setField('guardian_national_id', v)} />
                  <Field label="ملاحظات (اختياري)" value={person.file_notes} onChange={(v) => setField('file_notes', v)} />
                </div>
                {quote && <p className="public-contract-privacy"><Wallet size={16} />{quote.package_title} — {money(quote.payable_total, quote.currency)}، والطالب هيدفع العربون من حسابه.</p>}
                <div className="public-contract-actions">
                  <button type="button" className="portal-button portal-button-outline" onClick={() => goTo(0)}><ArrowRight size={17} />رجوع للباقة</button>
                  <button type="button" className="portal-button portal-button-primary" disabled={submitting} onClick={() => void register()}>
                    {submitting ? <LoaderCircle className="portal-spin" size={17} /> : <UserPlus size={17} />}{submitting ? 'جاري إنشاء الحساب...' : 'إنشاء حساب الطالب والعقد'}
                  </button>
                </div>
              </section>
            )}

            {step === 2 && created && (
              <section className="public-contract-review">
                <div className="portal-contract-note"><CheckCircle2 size={18} /><span>تم إنشاء حساب {created.name} والعقد رقم {created.application_number}.</span></div>
                <div className="portal-card portal-card-body public-contract-credentials">
                  <h2 className="public-contract-heading"><KeyRound size={20} /> بيانات دخول الطالب</h2>
                  {[['url', 'رابط الدخول', LOGIN_URL], ['email', 'البريد الإلكتروني', created.email], ['password', 'كلمة المرور', created.password]].map(([key, label, value]) => (
                    <div key={key} className="public-contract-credential">
                      <div><small>{label}</small><b dir="ltr">{value}</b></div>
                      <button type="button" className="portal-button portal-button-outline" onClick={() => void copy(key, value)}>{copied === key ? <Check size={16} /> : <Copy size={16} />}</button>
                    </div>
                  ))}
                  <p className="public-contract-privacy"><MailCheck size={16} />اتبعت نفس البيانات على إيميل الطالب. كلمة المرور بتظهر هنا مرة واحدة بس، انسخها دلوقتي.</p>
                  <div className="public-contract-message">
                    <pre>{message}</pre>
                    <button type="button" className="portal-button portal-button-primary" onClick={() => void copy('message', message)}>{copied === 'message' ? <Check size={16} /> : <Copy size={16} />}نسخ رسالة واتساب</button>
                  </div>
                </div>
                <div className="public-contract-actions public-contract-actions-top">
                  <button type="button" className="portal-button portal-button-primary" disabled={downloading} onClick={() => void downloadPdf()}>{downloading ? <LoaderCircle className="portal-spin" size={17} /> : <Download size={17} />}تحميل العقد PDF</button>
                  <button type="button" className="portal-button portal-button-outline" onClick={startOver}><UserPlus size={17} />تسجيل طالب جديد</button>
                </div>
                <div className="portal-contract-viewport">
                  <ContractDocument contract={created.contract} details={created.contract.details as ContractDetails} sections={sections} documentRef={documentRef} />
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', dir = 'auto' }: { label: string; value: string; onChange: (v: string) => void; type?: string; dir?: string }) {
  const id = `pc-${label.replace(/[^\p{L}\p{N}]+/gu, '-')}`;
  return <div className="portal-field"><label htmlFor={id}>{label}</label><input id={id} className="portal-input" type={type} dir={dir} value={value} autoComplete="off" onChange={(e) => onChange(e.target.value)} /></div>;
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  const id = `pc-${label.replace(/[^\p{L}\p{N}]+/gu, '-')}`;
  return <div className="portal-field"><label htmlFor={id}>{label}</label><select id={id} className="portal-select" value={value} onChange={(e) => onChange(e.target.value)}><option value="">اختر من القائمة</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
}
